/**
 * What the credits screen lists (spec §18, Phase 4b). Names, links and licences are taken from
 * what the repository already records: assets-src/LICENSES.md (art), ADR-0024 and
 * docs/runbooks/weather.md (weather data) and the libraries' own package licences.
 * Product and licence names are not translated; what each item is used for is (`usedForKey`).
 */

export type CreditSectionId = 'art' | 'data' | 'software';

export interface CreditEntry {
  /** Stable id, also the React key. */
  id: string;
  /** Product or pack name as its author publishes it. */
  name: string;
  author: string;
  /** Where to find the work. */
  url: string;
  /** Licence name as recorded in the repo. */
  licence: string;
  /** Link to the licence text, when it is public. */
  licenceUrl?: string;
  /** i18n key (under `credits.usedFor`) saying what the game uses it for. */
  usedForKey: string;
}

export interface CreditSection {
  id: CreditSectionId;
  /** i18n key for the section heading. */
  titleKey: string;
  entries: readonly CreditEntry[];
}

const CC_BY_4 = 'https://creativecommons.org/licenses/by/4.0/';
const MIT = 'https://opensource.org/license/mit';

export const CREDIT_SECTIONS: readonly CreditSection[] = [
  {
    id: 'art',
    titleKey: 'credits.sections.art',
    entries: [
      {
        id: 'limezu-modern-office',
        name: 'Modern Office',
        author: 'LimeZu',
        url: 'https://limezu.itch.io/modernoffice',
        // The licence text ships with the purchase (tile_assets/LICENSE.txt); it has no public URL.
        licence: 'Modern Office License',
        usedForKey: 'credits.usedFor.officeTiles',
      },
      {
        id: 'limezu-modern-interiors',
        name: 'Modern Interiors',
        author: 'LimeZu',
        url: 'https://limezu.itch.io/moderninteriors',
        // Purchased pack (tile_assets/04_Characters); its terms are on the store page, no licence file.
        licence: 'Modern Interiors License',
        usedForKey: 'credits.usedFor.characters',
      },
    ],
  },
  {
    id: 'data',
    titleKey: 'credits.sections.data',
    entries: [
      {
        id: 'smhi',
        name: 'SMHI Open Data',
        author: 'SMHI',
        url: 'https://www.smhi.se/data/oppna-data',
        licence: 'CC BY 4.0',
        licenceUrl: CC_BY_4,
        usedForKey: 'credits.usedFor.weather',
      },
      {
        id: 'open-meteo',
        name: 'Open-Meteo',
        author: 'Open-Meteo.com',
        url: 'https://open-meteo.com/',
        licence: 'CC BY 4.0',
        licenceUrl: CC_BY_4,
        usedForKey: 'credits.usedFor.weatherFallback',
      },
    ],
  },
  {
    id: 'software',
    titleKey: 'credits.sections.software',
    entries: [
      {
        id: 'phaser',
        name: 'Phaser',
        author: 'Richard Davey, Phaser Studio Inc.',
        url: 'https://phaser.io/',
        licence: 'MIT',
        licenceUrl: MIT,
        usedForKey: 'credits.usedFor.gameEngine',
      },
      {
        id: 'react',
        name: 'React',
        author: 'Meta Platforms, Inc.',
        url: 'https://react.dev/',
        licence: 'MIT',
        licenceUrl: MIT,
        usedForKey: 'credits.usedFor.ui',
      },
      {
        id: 'colyseus',
        name: 'Colyseus',
        author: 'Endel Dreyer',
        url: 'https://colyseus.io/',
        licence: 'MIT',
        licenceUrl: MIT,
        usedForKey: 'credits.usedFor.multiplayer',
      },
      {
        id: 'i18next',
        name: 'i18next',
        author: 'i18next',
        url: 'https://www.i18next.com/',
        licence: 'MIT',
        licenceUrl: MIT,
        usedForKey: 'credits.usedFor.translations',
      },
    ],
  },
];

/** Every entry in display order. */
export function allCredits(sections: readonly CreditSection[] = CREDIT_SECTIONS): CreditEntry[] {
  return sections.flatMap((section) => section.entries);
}

/** Every i18n key the credits screen needs, so a test can check they exist. */
export function creditTranslationKeys(
  sections: readonly CreditSection[] = CREDIT_SECTIONS,
): string[] {
  return [
    ...sections.map((section) => section.titleKey),
    ...allCredits(sections).map((entry) => entry.usedForKey),
  ];
}

/** Only https links leave the game; anything else is a mistake in the list above. */
export function isSafeExternalUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}
